import { FC, ReactNode, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { css, keyframes, Theme } from '@emotion/react';
import type { DeploymentMetricsResponse } from 'api/deployment/handlers/deploymentMetricsHandler';
import { DeploymentMetricType } from 'common/CommonConstants';
import { DeploymentMetricsRequest } from 'common/deployment/DeploymentApiRoutesDocuments';
import type { DeploymentMetricsSpectrumRow } from 'common/deployment/MetricsDocument';
import Api from 'client/lib/Api';
import { Typography } from 'client/components/Typography';
import { Easing, borderRadius } from 'client/Theme';
import { expandDataToFillSlices, dataRowToProps } from './spectrumUtils';

const containerStyle = (theme: Theme) => css`
  position: relative;
  width: 100%;
  height: 56px;
  max-width: 65ch;
  background-color: ${theme.color.grey[60]};
  border-radius: ${borderRadius}px;
  overflow: hidden;

  .title {
    position: absolute;
    top: 0;
    left: 0;
    height: 6px;
    font-size: 1rem;
    font-weight: 700;
    transform: translate(6px, 7px) scale(0.75);
    color: ${theme.color.grey[100]};
    user-select: none;
  }

  .period-select {
    position: absolute;
    top: 0;
    right: 4px;
    height: 6px;
    font-size: 1rem;
    font-weight: 600;
    transform: translate(6px, 7px) scale(0.75);
    user-select: none;
    color: ${theme.color.grey[80]};

    > span {
      padding: 0px 5px;
      cursor: pointer;

      &.selected {
        font-weight: bold;
        color: ${theme.color.grey[100]};
      }
    }
  }
`;

const pulseAnimation = keyframes`
  0% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const spectrumStyle = (theme: Theme) => css`
  position: absolute;
  display: flex;
  justify-content: flex-end;
  padding-right: 20px;
  bottom: 0;
  width: calc(100% + 6px);
  height: 26px;

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 50px;
    height: 40px;
    background: linear-gradient(to left, transparent 0%, ${theme.color.grey[60]} 95%);
    z-index: 1;
    pointer-events: none;
  }

  > div {
    position: relative;
    flex-shrink: 0;
    margin: 0 1px;
    margin-top: 4px;
    width: 6px;
    height: 100%;
    background-color: ${theme.color.green[100]};
    border-radius: 2px 2px 0 0;
    opacity: 0.5;

    &.warning,
    &.error {
      margin-top: 8px;
      opacity: 1;
    }

    &.yellow {
      background-color: ${theme.color.yellow[100]};
    }

    &.red {
      background-color: ${theme.color.red[100]};
    }

    &.gray {
      background-color: ${theme.color.grey[80]};
    }

    &:last-of-type {
      margin-top: 0;
      margin-left: 0;
      opacity: 1;

      animation: ${pulseAnimation} 1s infinite alternate ${Easing.springy};
      transform: translateX(1px);
    }

    > .worst-status {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 10px;
      background-color: ${theme.color.green[100]};

      &.yellow {
        background-color: ${theme.color.yellow[100]};
      }

      &.red {
        background-color: ${theme.color.red[100]};
      }

      &.gray {
        background-color: ${theme.color.grey[80]};
      }
    }

    &:last-child {
      margin-right: 0;
    }
  }
`;

const loadSpectrumData = async (deploymentID: number, period: string, slices: number) => {
  const response = await Api.post<DeploymentMetricsRequest, DeploymentMetricsResponse>('/deployment/metrics', {
    deploymentID,
    type: DeploymentMetricType.SPECTRUM,
    resolutionMinutes: period == '1m' ? 1 : period == '1h' ? 60 : 24 * 60,
  });

  if (response.status !== 'ok') {
    throw new Error('Failed to load spectrum metrics');
  }

  const data = (response.data as DeploymentMetricsSpectrumRow[]).slice(-slices);
  return data;
};

interface SpectrumItemProps {
  title: string;
  major: string;
  minor: string;
}

const SpectrumItem: FC<SpectrumItemProps> = ({ title, major, minor }) => (
  <div title={title} className={major}>
    {major !== minor && <div className={'worst-status ' + minor} />}
  </div>
);

export interface SpectrumProps {
  deploymentID?: number;
  label: string | JSX.Element;
  slices: number;
  className?: string;
  children?: ReactNode;
}

export const Spectrum: FC<SpectrumProps> = ({ deploymentID, label, slices, children, ...props }) => {
  const [period, setPeriod] = useState<'1m' | '1h' | '1d'>('1m');

  const query = useQuery({
    // Cache query based on deploymentID, period & slices
    queryKey: ['metrics.spectrum', deploymentID, period, slices],
    queryFn: () => loadSpectrumData(deploymentID!, period, slices),
    enabled: !!deploymentID,
    retryDelay: 10000,

    // Refresh after 10 seconds
    refetchInterval: 10000,
  });

  const items = useMemo(
    () => expandDataToFillSlices(query.data || [], slices, period, 'spectrum').map(dataRowToProps),
    [slices, query.data, period]
  );

  return (
    <div css={containerStyle} className="MuiFormControl-root" {...props}>
      <Typography variant="body2" component="label" className="title">
        {label}
      </Typography>

      <Typography variant="body2" component="label" className="period-select">
        {(['1m', '1h', '1d'] as const).map((value, index) => (
          <span key={index} onClick={() => setPeriod(value)} className={period === value ? 'selected' : ''}>
            {value}
          </span>
        ))}
      </Typography>

      <div css={spectrumStyle}>
        {items.map((props) => (
          <SpectrumItem {...props} />
        ))}
      </div>
    </div>
  );
};
